import StorageLocal from "@homer.core/storage.local";
import StorageSession from "@homer.core/storage.session";
import page from "@homer.core/page";
import user from "@homer.core/user";
import Channel from "@homer.core/channel";
import filterUndefined from "@homer.utility/object.filter.undefined";
import isUndefined from "@homer.utility/validator.isundefined";
import isFunction from "@homer.utility/validator.isfunction";
import { name } from "../package.json";

const storageLocal = new StorageLocal(name);
const storageSession = new StorageSession(name);
const stateLocal = JSON.parse(storageLocal.get() || "{}");
const stateSession = JSON.parse(storageSession.get() || "{}");
const channel = Channel.obtain(name);
const url = new URL(location.href);
const parameter = url.parameter;
const timestamp = +new Date();

const processGoogle = ((gclidFallback) => {
    const matchPlus = /\+/g;
    const matchGclid = /^[\w-]+$/;
    const matchDclid = /^[\w-]+$/;
    const matchBraid = /^[\w-]+$/;
    const matchGadSource = /^[\w-]+$/;
    const matchGadSourceHash = /^#gad_source[_=](\d+)$/;

    const decodeHashComponent = (component = "") => {
        return decodeURIComponent(component.replace(matchPlus, " "));
    };

    const decodeHash = (hash = "#") => {
        return (hash || "#")
            .slice(1)
            .split("&")
            .reduce((accumulator, item) => {
                let [key, ...value] = item.split("=");
                key = decodeHashComponent(key);
                value = decodeHashComponent(value.join("="));

                if (key && !accumulator.has(key)) {
                    accumulator.set(key, value);
                }

                return accumulator;
            }, new Map());
    };

    const sanitizeWbraid = (braid) => {
        if (!braid) {
            return undefined;
        }

        const decoded = (() => {
            try {
                return decodeURIComponent(braid);
            } catch {
                return braid;
            }
        })().split(",");

        return decoded.length === 2 && decoded[0] === decoded[1] ? decoded[0] : braid;
    };

    const process = (url, referrer = undefined, isReferrer = false) => {
        const parameter = url.searchParams;
        const gadSourceQuery = isReferrer
            ? undefined
            : ((value) => (matchGadSource.test(value) && value) || undefined)(parameter.get("gad_source") || "");
        const gadSourceHash = isReferrer ? undefined : (url.hash.match(matchGadSourceHash) || [])[1] || undefined;
        let gclid, gclsrc, dclid, gbraid, wbraid, gad_campaignid, gad_source, gad_source_src;

        gclid = parameter.get("gclid");
        gclsrc = parameter.get("gclsrc");
        dclid = parameter.get("dclid");
        gbraid = parameter.get("gbraid");
        wbraid = sanitizeWbraid(parameter.get("wbraid"));
        gad_campaignid = isReferrer ? undefined : parameter.get("gad_campaignid") || undefined;
        gad_source = gadSourceQuery || gadSourceHash;
        gad_source_src = isReferrer
            ? undefined
            : gadSourceQuery && gadSourceHash
              ? 1
              : gadSourceQuery
                ? 2
                : gadSourceHash
                  ? 3
                  : undefined;

        if (!isReferrer && !(gclid && gclsrc && wbraid && gbraid)) {
            const hash = decodeHash(url.hash);

            gclid = gclid || hash.get("gclid");
            gclsrc = gclsrc || hash.get("gclsrc");
            // dclid does not have a hash fallback!
            gbraid = gbraid || hash.get("gbraid");
            wbraid = wbraid || hash.get("wbraid");
        }

        gclid = gclid && matchGclid.test(gclid) ? gclid : gclidFallback || undefined;
        gclsrc = gclsrc || undefined;
        dclid = dclid && matchDclid.test(dclid) ? dclid : undefined;
        gbraid = !gclid && !dclid && gbraid && matchBraid.test(gbraid) ? gbraid : undefined;
        wbraid = !gclid && !dclid && wbraid && matchBraid.test(wbraid) ? wbraid : undefined;

        if (!isReferrer && referrer && !(gclid || gclsrc || dclid || gbraid || wbraid || gad_source)) {
            return process(referrer, undefined, true);
        }

        return filterUndefined({
            gclid,
            gclsrc,
            dclid,
            gbraid,
            wbraid,
            gad_campaignid,
            gad_source,
            gad_source_src,
        });
    };

    return process;
})((!stateLocal.google && stateLocal?.gclid?.value) || undefined);

const processKlaviyo = (kx) => {
    const matchToken = /^([\w\-_=]+)\.(\w+)$/;
    const klaviyoID = ((node) => {
        return node ? node.getAttribute("content") : null;
    })(document.head.querySelector(':scope > meta[itemprop="klaviyo"]'));

    if (!matchToken.test(kx)) {
        return;
    }

    if (klaviyoID && kx.split(".")[1] !== klaviyoID) {
        return;
    }

    return kx;
};

class Attribution {
    constructor() {
        let changed = false;

        // Google
        if (parameter.gclid && (!stateLocal.gclid || stateLocal.gclid.value !== parameter.gclid)) {
            stateLocal.gclid = { timestamp, value: parameter.gclid };
            changed = true;
        }

        stateLocal.google = (() => {
            const value = processGoogle(url, page.referrer ? new URL(page.referrer) : undefined);

            if (
                !stateLocal.google ||
                value.gclid ||
                value.gclsrc ||
                value.dclid ||
                value.gbraid ||
                value.wbraid ||
                value.gad_campaignid ||
                value.gad_source ||
                value.gad_source_src
            ) {
                changed = true;

                // Keep aw.dv out of the synthesized attribution until we model gtag's feature gates.
                const hasAw = (value.gclid && (!value.gclsrc || value.gclsrc === "aw" || value.gclsrc === "aw.ds")) || false;
                const hasDc = !!value.dclid || (value.gclid && ["aw.ds", "ds", "3p.ds"].includes(value.gclsrc));

                value.gad_source = value.gad_source ?? (hasAw || hasDc ? 0 : undefined);

                if (typeof value.gad_source === "undefined") {
                    delete value.gad_source;
                }

                if (value.gad_source === 0) {
                    delete value.gad_source_src;
                }

                const bucket = ["gclaw", "gcldc", "gclgf", "gclha", "gclgb", "gclag", "gclgs"].reduce((accumulator, key) => {
                    accumulator[key] = {
                        add: (value) => {
                            bucket[key].values = bucket[key].values.reduce(
                                (accumulator, entry) => {
                                    if (entry !== value) {
                                        accumulator.push(entry);
                                    }

                                    return accumulator;
                                },
                                [value],
                            );

                            value = accumulator[key].values.join(".");

                            if (value.length > 1200) {
                                accumulator[key].values = value.substring(0, value.lastIndexOf(".")).split(".");
                            }
                        },
                        values: (stateLocal?.google?.value || {})[key] ? stateLocal.google.value[key].split(".") : [],
                    };

                    return accumulator;
                }, {});

                if (!isUndefined(value.gclid)) {
                    switch (value.gclsrc) {
                        case "aw.ds":
                            bucket.gclaw.add(value.gclid);
                            bucket.gcldc.add(value.gclid);

                            break;
                        case "aw.dv":
                            // gtag.js only maps aw.dv into AW/DC buckets behind internal feature gates.
                            break;
                        case "ds":
                        case "3p.ds":
                            bucket.gcldc.add(value.gclid);

                            break;
                        case "gf":
                            bucket.gclgf.add(value.gclid);

                            break;
                        case "ha":
                            bucket.gclha.add(value.gclid);

                            break;
                        default:
                            bucket.gclaw.add(value.gclid);

                            break;
                    }
                }

                if (!isUndefined(value.dclid)) {
                    bucket.gcldc.add(value.dclid);
                }

                if (!isUndefined(value.gbraid)) {
                    bucket.gclag.add(value.gbraid);
                    bucket.gclgb.add(value.gbraid);
                }

                if (!isUndefined(value.wbraid)) {
                    bucket.gclgb.add(value.wbraid);
                }

                if (!isUndefined(value.gad_source)) {
                    bucket.gclgs.add(value.gad_source);
                }

                return {
                    timestamp,
                    source: page.uuid,
                    value: {
                        ...value,
                        bucket: Object.fromEntries(
                            Object.entries(bucket).map(([name, item]) => {
                                return [name, item.values.join(".")];
                            }),
                        ),
                    },
                };
            }

            return stateLocal?.google;
        })();

        // Bing
        if (parameter.msclkid && (!stateLocal.msclkid || stateLocal.msclkid.value !== parameter.msclkid)) {
            stateLocal.msclkid = { timestamp, value: parameter.msclkid };
            changed = true;
        }

        // Meta/Facebook
        if (parameter.fbclid && (!stateLocal.fbclid || stateLocal.fbclid.value !== parameter.fbclid)) {
            stateLocal.fbclid = { timestamp, value: parameter.fbclid };
            changed = true;
        }

        // Pinterest
        if (parameter.epik && (!stateLocal.epik || stateLocal.epik.value !== parameter.epik)) {
            stateLocal.epik = { timestamp, value: parameter.epik };
            changed = true;
        }

        // OpenAI
        if (parameter.oppref && (!stateLocal.oppref || stateLocal.oppref.value !== parameter.oppref)) {
            stateLocal.oppref = { timestamp, value: parameter.oppref };
            changed = true;
        }

        // TikTok
        if (parameter.ttclid && (!stateLocal.ttclid || stateLocal.ttclid.value !== parameter.ttclid)) {
            // must expire after 30 days
            stateLocal.ttclid = {
                timestamp,
                expires: timestamp + 30 * 24 * 60 * 60 * 1000,
                value: parameter.ttclid,
            };
            changed = true;
        }

        // Twitter/X
        if (parameter.twclid && (!stateLocal.twclid || stateLocal.twclid.value !== parameter.twclid)) {
            // does, afaik, not expire
            stateLocal.twclid = { timestamp, value: parameter.twclid };
            changed = true;
        }

        // Reddit
        if (parameter.rdt_cid && (!stateLocal.rdt_cid || stateLocal.rdt_cid.value !== parameter.rdt_cid)) {
            stateLocal.rdt_cid = { timestamp, value: parameter.rdt_cid };
            changed = true;
        }

        // Snapchat
        if (parameter.ScCid && (!stateLocal.ScCid || stateLocal.ScCid.value !== parameter.ScCid)) {
            stateLocal.ScCid = { timestamp, value: parameter.ScCid };
            changed = true;
        }

        // klaviyo
        if (parameter._kx) {
            const kx = processKlaviyo(parameter._kx);

            if (kx && (!stateSession._kx || stateSession._kx.value !== kx)) {
                stateSession._kx = { timestamp, value: kx };
                changed = true;
            }
        }

        // UTM (utilized by Google/Bing/Meta/TikTok/Pinterest)
        if (parameter.utm_source) {
            if (
                !stateLocal.utm ||
                stateLocal.utm.value.id !== parameter.utm_id ||
                stateLocal.utm.value.source !== parameter.utm_source ||
                stateLocal.utm.value.medium !== parameter.utm_medium ||
                stateLocal.utm.value.campaign !== parameter.utm_campaign ||
                stateLocal.utm.value.term !== parameter.utm_term ||
                stateLocal.utm.value.content !== parameter.utm_content ||
                stateLocal.utm.value.format !== parameter.utm_creative_format ||
                stateLocal.utm.value.tactic !== parameter.utm_marketing_tactic
            ) {
                stateLocal.utm = {
                    timestamp,
                    value: {
                        id: parameter.utm_id || undefined,
                        source: parameter.utm_source || undefined,
                        medium: parameter.utm_medium || undefined,
                        campaign: parameter.utm_campaign || undefined,
                        term: parameter.utm_term || undefined,
                        content: parameter.utm_content || undefined,
                        format: parameter.utm_creative_format || undefined,
                        tactic: parameter.utm_marketing_tactic || undefined,
                    },
                };
                changed = true;
            }
        }

        Object.defineProperties(this, {
            gclid: {
                get: () => Object.freeze(stateLocal.gclid),
                enumerable: true,
            },
            google: {
                get: () => Object.freeze(stateLocal.google),
                enumerable: true,
            },
            gsaexp: {
                get: () => {
                    if (isFunction(globalThis?.__gsaExp?.id)) {
                        try {
                            const gsaexp = Number(globalThis.__gsaExp.id());

                            return !isNaN(gsaexp) ? gsaexp : undefined;
                        } catch {}
                    }

                    return undefined;
                },
                enumerable: true,
            },
            msclkid: {
                get: () => Object.freeze(stateLocal.msclkid),
                enumerable: true,
            },
            fbclid: {
                get: () => Object.freeze(stateLocal.fbclid),
                enumerable: true,
            },
            epik: {
                get: () => Object.freeze(stateLocal.epik),
                enumerable: true,
            },
            oppref: {
                get: () => Object.freeze(stateLocal.oppref),
                enumerable: true,
            },
            ttclid: {
                get: () => Object.freeze(stateLocal.ttclid),
                enumerable: true,
            },
            twclid: {
                get: () => Object.freeze(stateLocal.twclid),
                enumerable: true,
            },
            rdt_cid: {
                get: () => Object.freeze(stateLocal.rdt_cid),
                enumerable: true,
            },
            ScCid: {
                get: () => Object.freeze(stateLocal.ScCid),
                enumerable: true,
            },
            _kx: {
                get: () => {
                    if (stateSession._kx && !processKlaviyo(stateSession._kx.value)) {
                        stateSession._kx = undefined;
                        storageSession.set(JSON.stringify(stateSession));
                        channel.broadcast({
                            session: stateSession,
                            clear: { session: { _kx: true } },
                        });
                    }

                    return Object.freeze(stateSession._kx);
                },
                enumerable: true,
            },
            utm: {
                get: () => Object.freeze(stateLocal.utm),
                enumerable: true,
            },
        });

        channel.on("broadcast", (event, payload) => {
            const updateSessionStorage = payload.session || payload.clear?.session?._kx;

            Object.assign(stateLocal, payload.local || {});
            Object.assign(stateSession, payload.session || {});

            if (payload.clear?.session?._kx) {
                stateSession._kx = undefined;
            }

            if (updateSessionStorage) {
                storageSession.set(JSON.stringify(stateSession));
            }
        });

        Promise.resolve(user).then((user) => {
            user.on(/^(?:login|logout)$/, () => {
                if (!stateSession._kx) {
                    return;
                }

                stateSession._kx = undefined;

                storageSession.set(JSON.stringify(stateSession));

                channel.broadcast({
                    session: stateSession,
                    clear: { session: { _kx: true } },
                });
            });
        });

        if (changed) {
            storageLocal.set(JSON.stringify(stateLocal));
            storageSession.set(JSON.stringify(stateSession));

            channel.broadcast({ local: stateLocal, session: stateSession });
        }

        return Object.freeze(this);
    }
}

export default new Attribution();
